import { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import Header from "../Header";
import Footer from "../Footer";
import useSeo from "../../hooks/useSeo";
import { assetUrl } from "../../utils/api";
import { secondhandGet, secondhandPatch } from "../../api/secondhandApi";
import GoodsChannelTabs from "../shop/GoodsChannelTabs";

export default function SecondhandMinePage() {
  const navigate = useNavigate();
  const location = useLocation();
  const userId = Number(localStorage.getItem("user_id") || 0);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState(() => location.state?.flash || "");

  useSeo({ title: "我的闲置 - 生活好物", path: "/secondhand/mine", noindex: true });

  useEffect(() => {
    if (!userId) {
      navigate(`/login?redirect=${encodeURIComponent(location.pathname)}`, { replace: true });
    }
  }, [userId, navigate, location.pathname]);

  const load = () => {
    if (!userId) return;
    setLoading(true);
    secondhandGet(`/mine?user_id=${userId}`)
      .then((data) => setItems(data.items || []))
      .catch(() => setItems([]))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId]);

  const setStatus = async (id, status) => {
    try {
      await secondhandPatch(`/listings/${id}/status`, { user_id: userId, status });
      setMessage("已更新");
      load();
    } catch (e) {
      alert(e.message || "更新失败");
    }
  };

  if (!userId) return null;

  return (
    <div className="min-h-screen bg-gradient-to-br from-rose-50 via-pink-50 to-amber-50 flex flex-col">
      <Header />
      <main className="flex-1 px-4 py-8">
        <div className="max-w-3xl mx-auto space-y-6">
          <header className="text-center space-y-3">
            <h1 className="text-2xl font-bold text-gray-800">我的闲置</h1>
            <GoodsChannelTabs />
            <Link
              to="/secondhand/new"
              className="inline-flex px-5 py-2 rounded-full bg-rose-600 text-white text-sm"
            >
              再发一条
            </Link>
          </header>

          {message && <div className="text-sm text-center text-blue-600">{message}</div>}

          {loading ? (
            <div className="text-center text-gray-400 py-16">加载中...</div>
          ) : items.length === 0 ? (
            <div className="bg-white rounded-2xl p-10 text-center text-gray-400">还没有发布</div>
          ) : (
            <div className="space-y-3">
              {items.map((item) => (
                <div
                  key={item.id}
                  className="bg-white rounded-2xl border border-pink-50 p-4 flex gap-4"
                >
                  <div className="w-20 h-20 rounded-xl overflow-hidden bg-rose-50 shrink-0">
                    {item.cover_url ? (
                      <img src={assetUrl(item.cover_url)} alt="" className="w-full h-full object-cover" />
                    ) : null}
                  </div>
                  <div className="flex-1 min-w-0 space-y-2">
                    <Link to={`/secondhand/${item.id}`} className="font-medium text-gray-800 hover:text-pink-600">
                      {item.title}
                    </Link>
                    <p className="text-xs text-gray-400">
                      {item.status_label} · {item.location_label || "未填城市"}
                    </p>
                    <div className="flex flex-wrap gap-2 text-xs">
                      {item.status === "pending" && (
                        <span className="text-amber-600">审核中，通过后公开展示</span>
                      )}
                      {item.status === "rejected" && (
                        <>
                          <span className="text-red-500">未通过审核</span>
                          <button
                            type="button"
                            onClick={() => setStatus(item.id, "pending")}
                            className="px-3 py-1 rounded-full border text-pink-600"
                          >
                            重新提交审核
                          </button>
                        </>
                      )}
                      {item.status === "published" && (
                        <>
                          <button
                            type="button"
                            onClick={() => setStatus(item.id, "reserved")}
                            className="px-3 py-1 rounded-full border"
                          >
                            标记预留
                          </button>
                          <button
                            type="button"
                            onClick={() => setStatus(item.id, "done")}
                            className="px-3 py-1 rounded-full border"
                          >
                            已成交
                          </button>
                          <button
                            type="button"
                            onClick={() => setStatus(item.id, "taken_down")}
                            className="px-3 py-1 rounded-full border text-red-500"
                          >
                            下架
                          </button>
                        </>
                      )}
                      {item.status === "reserved" && (
                        <>
                          <button
                            type="button"
                            onClick={() => setStatus(item.id, "published")}
                            className="px-3 py-1 rounded-full border text-pink-600"
                          >
                            取消预留
                          </button>
                          <button
                            type="button"
                            onClick={() => setStatus(item.id, "done")}
                            className="px-3 py-1 rounded-full border"
                          >
                            已成交
                          </button>
                        </>
                      )}
                      {(item.status === "taken_down" || item.status === "done") && (
                        <button
                          type="button"
                          onClick={() => setStatus(item.id, "pending")}
                          className="px-3 py-1 rounded-full border text-pink-600"
                        >
                          重新提交审核
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </main>
      <Footer />
    </div>
  );
}
